import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} VariantDisplayTitleRefs
 * @property {HTMLElement} title
 */

/**
 * Product H1 that stays on the product title while required variant dropdowns
 * are incomplete, then switches to custom.variant_display_name (map built in Liquid).
 *
 * @extends {Component<VariantDisplayTitleRefs>}
 */
class VariantDisplayTitle extends Component {
  requiredRefs = ['title'];

  /** @type {Record<string, string>} */
  #variantNames = {};

  /** @type {string} */
  #productTitle = '';

  /** @type {HTMLElement | Document | null} */
  #sectionRoot = null;

  connectedCallback() {
    super.connectedCallback();

    this.#productTitle = this.dataset.productTitle || '';
    this.#variantNames = this.#parseVariantNames(this.dataset.variantNames);
    this.#sectionRoot = this.closest('.shopify-section, dialog') || this.closest('[id*="ProductInformation-"]') || document;

    const root = this.#sectionRoot;
    // Listen on the section so we still work if the picker mounts after this block.
    root.addEventListener('change', this.#onPickerChange);
    root.addEventListener('variant-picker:auto-select', this.#onAutoSelect);
    root.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    this.#syncFromPicker();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const root = this.#sectionRoot;
    if (root) {
      root.removeEventListener('change', this.#onPickerChange);
      root.removeEventListener('variant-picker:auto-select', this.#onAutoSelect);
      root.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    }

    this.#sectionRoot = null;
  }

  /**
   * @param {string | undefined} raw
   * @returns {Record<string, string>}
   */
  #parseVariantNames(raw) {
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        /** @type {Record<string, string>} */
        const map = {};
        for (const [key, value] of Object.entries(parsed)) {
          if (typeof value === 'string' && value.length > 0) {
            map[this.#normalizeId(key)] = value;
          }
        }
        return map;
      }
    } catch {
      // Ignore malformed JSON from Liquid; keep empty map.
    }
    return {};
  }

  /**
   * Normalize numeric / GID-looking ids to a consistent string key.
   * @param {string | number | null | undefined} id
   * @returns {string}
   */
  #normalizeId(id) {
    if (id == null) return '';
    const asString = String(id).trim();
    if (!asString) return '';

    // gid://shopify/ProductVariant/123456 → 123456
    if (asString.includes('/')) {
      const segment = asString.split('/').pop();
      if (segment) return segment;
    }

    const digits = asString.match(/\d+/);
    return digits ? digits[0] : asString;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #findVariantPicker() {
    const productId = this.dataset.productId;
    const root = this.#sectionRoot || document;

    if (productId) {
      const match = root.querySelector(`variant-picker[data-product-id="${productId}"]`);
      if (match instanceof HTMLElement) return match;
    }

    const fallback = root.querySelector('variant-picker');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  /**
   * @param {HTMLElement | null} picker
   * @returns {boolean}
   */
  #hasIncompleteRequiredDropdowns(picker) {
    if (!picker) return false;

    // Prefer the picker's own getter when available.
    if ('hasRequiredDropdownPlaceholderSelection' in picker) {
      try {
        return Boolean(
          /** @type {{ hasRequiredDropdownPlaceholderSelection: boolean }} */ (picker)
            .hasRequiredDropdownPlaceholderSelection
        );
      } catch {
        // Fall through to DOM inspection.
      }
    }

    if (picker.dataset.requireDropdownSelections !== 'true') return false;

    const selects = Array.from(picker.querySelectorAll('select.variant-option__select')).filter(
      (select) => select instanceof HTMLSelectElement
    );

    return selects.some((select) => select instanceof HTMLSelectElement && select.value === '');
  }

  /**
   * Resolve the currently selected variant id from the picker or URL.
   * @param {HTMLElement | null} picker
   * @returns {string}
   */
  #resolveSelectedVariantId(picker) {
    if (!picker) {
      return this.#normalizeId(new URLSearchParams(window.location.search).get('variant'));
    }

    // Prefer getSelectedSkuVariant when the picker exposes it (SKU-filtered dropdowns).
    if (
      'getSelectedSkuVariant' in picker &&
      typeof /** @type {{ getSelectedSkuVariant?: () => { id?: string | number } | undefined }} */ (picker)
        .getSelectedSkuVariant === 'function'
    ) {
      const skuVariant = /** @type {{ getSelectedSkuVariant: () => { id?: string | number } | undefined }} */ (
        picker
      ).getSelectedSkuVariant();
      if (skuVariant?.id != null) {
        return this.#normalizeId(skuVariant.id);
      }
    }

    const selects = Array.from(picker.querySelectorAll('select.variant-option__select')).filter(
      (select) => select instanceof HTMLSelectElement
    );

    if (selects.length && selects.every((select) => select instanceof HTMLSelectElement && select.value !== '')) {
      for (const select of selects) {
        if (!(select instanceof HTMLSelectElement)) continue;
        const option = select.selectedOptions[0];
        const variantId = option?.dataset.variantId;
        if (variantId) return this.#normalizeId(variantId);
      }
    }

    return this.#normalizeId(new URLSearchParams(window.location.search).get('variant'));
  }

  /**
   * Desktop title lives in this block; mobile shows a separate H1 in
   * `.product-information__mobile-title` and hides the in-details group that
   * contains this element. Keep both in sync.
   * @param {string} text
   */
  #setTitleText(text) {
    const titleEl = this.refs.title || this.querySelector('h1, [ref="title"]');
    if (titleEl instanceof HTMLElement && titleEl.textContent !== text) {
      titleEl.textContent = text;
    }

    const section =
      this.closest('.product-information, .shopify-section') || this.#sectionRoot || document;
    if (!(section instanceof Element)) return;

    const mobileTitles = section.querySelectorAll(
      '.product-information__mobile-title h1, .product-information__mobile-title .variant-display-title__heading'
    );
    for (const mobileTitle of mobileTitles) {
      if (mobileTitle instanceof HTMLElement && mobileTitle.textContent !== text) {
        mobileTitle.textContent = text;
      }
    }
  }

  #showProductTitle() {
    this.#setTitleText(this.#productTitle);
  }

  /**
   * @param {string} variantId
   * @param {string} [fallbackTitle]
   */
  #showVariantDisplayName(variantId, fallbackTitle) {
    const key = this.#normalizeId(variantId);
    const fromMap = key ? this.#variantNames[key] : undefined;

    if (fromMap) {
      this.#setTitleText(fromMap);
      return;
    }

    if (fallbackTitle) {
      this.#setTitleText(fallbackTitle);
      return;
    }

    this.#showProductTitle();
  }

  #syncFromPicker() {
    const picker = this.#findVariantPicker();

    if (this.#hasIncompleteRequiredDropdowns(picker)) {
      this.#showProductTitle();
      return;
    }

    const variantId = this.#resolveSelectedVariantId(picker);
    if (variantId) {
      this.#showVariantDisplayName(variantId);
    } else {
      this.#showProductTitle();
    }
  }

  /**
   * Incomplete required dropdowns never emit variant:update — catch them on change.
   * @param {Event} event
   */
  #onPickerChange = (event) => {
    const picker = this.#findVariantPicker();
    if (!picker || !(event.target instanceof Node) || !picker.contains(event.target)) return;

    if (this.#hasIncompleteRequiredDropdowns(picker)) {
      this.#showProductTitle();
    }
    // Complete selections are handled by variant:update / auto-select.
  };

  /**
   * @param {Event} event
   */
  #onAutoSelect = (event) => {
    const detail =
      event instanceof CustomEvent
        ? /** @type {{ productId?: string, complete?: boolean }} */ (event.detail || {})
        : {};

    if (detail.productId && this.dataset.productId && detail.productId !== this.dataset.productId) {
      return;
    }

    if (detail.complete === false) {
      this.#showProductTitle();
      return;
    }

    if (detail.complete === true) {
      const picker = this.#findVariantPicker();
      const variantId = this.#resolveSelectedVariantId(picker);
      if (variantId) {
        this.#showVariantDisplayName(variantId);
      }
    }
  };

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail =
      /** @type {{ resource?: { id?: string | number, title?: string }, data?: { productId?: string, newProduct?: { id?: string } } }} */ (
        /** @type {CustomEvent} */ (event).detail || {}
      );

    const eventProductId = detail?.data?.productId;
    const target = event.target;

    if (detail?.data?.newProduct?.id) {
      // Combined listing product switch — leave title until selection resolves.
    } else if (eventProductId && this.dataset.productId && eventProductId !== this.dataset.productId) {
      return;
    } else if (
      target instanceof HTMLElement &&
      target.dataset.productId &&
      this.dataset.productId &&
      target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const picker = this.#findVariantPicker();
    if (this.#hasIncompleteRequiredDropdowns(picker)) {
      this.#showProductTitle();
      return;
    }

    const resource = detail?.resource;
    if (!resource) {
      this.#showProductTitle();
      return;
    }

    const variantId = this.#normalizeId(resource.id);
    const fallbackTitle = typeof resource.title === 'string' ? resource.title : undefined;
    this.#showVariantDisplayName(variantId, fallbackTitle);
  };
}

if (!customElements.get('variant-display-title')) {
  customElements.define('variant-display-title', VariantDisplayTitle);
}
